import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ArrowRight, Eye, EyeOff, Building2, Loader2 } from "lucide-react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { getInviteDetailsFromToken, type InviteDetails } from "@/utils/inviteUtils";
import { normalizeName } from "@/utils/sanitization";
import {
  AUTH_FIELD_CLASSES,
  AUTH_LABEL_CLASSES,
  AUTH_LINK_CLASSES,
  AUTH_PRIMARY_ACTION_CLASSES,
  AUTH_SECONDARY_ACTION_CLASSES,
  AuthPageHeader,
  AuthPageLayout,
} from "@/components/auth/AuthPageLayout";
import { GoogleSignInButton } from "@/components/auth/GoogleSignInButton";
import { EmailVerificationPending } from "@/components/auth/EmailVerificationPending";
import type { SignupFormData } from "@/hooks/useRobustSignup";
import { useRobustSignup } from "@/hooks/useRobustSignup";
import { cn } from "@/lib/utils";

export default function SignUp() {
  const [showPassword, setShowPassword] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const inviteToken = searchParams.get("invite_token");
  const [inviteDetails, setInviteDetails] = useState<InviteDetails | null>(null);
  const [loadingInvite, setLoadingInvite] = useState(!!inviteToken);

  const { toast } = useToast();
  const { t } = useLanguage();
  const { signUp, isLoading, showVerificationPending, userEmail, setShowVerificationPending } =
    useRobustSignup();

  // normalizeName rather than .trim(): a zero-width space passes trim() and min(2).
  const requiredName = z
    .string()
    .transform(normalizeName)
    .refine((name) => name.length > 0, { message: t.forms.validation.required })
    .refine((name) => name.length >= 2, {
      message: t.forms.validation.minLength.replace("{min}", "2"),
    });

  const formSchema = z.object({
    email: z.string().email({ message: t.forms.validation.email }),
    password: z.string().min(8, { message: t.forms.validation.minLength.replace("{min}", "8") }),
    firstName: requiredName,
    lastName: requiredName,
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
      firstName: "",
      lastName: "",
    },
  });

  // Fetch invite details if token is present
  useEffect(() => {
    const fetchInvite = async () => {
      if (!inviteToken) return;

      try {
        const details = await getInviteDetailsFromToken(inviteToken);
        if (details && details.isValid) {
          setInviteDetails(details);
          // Pre-fill the email field
          form.setValue("email", details.email);
        }
      } catch (error) {
        console.error("Failed to fetch invite details:", error);
      } finally {
        setLoadingInvite(false);
      }
    };

    fetchInvite();
  }, [inviteToken, form]);

  async function onSubmit(values: z.infer<typeof formSchema>) {
    const signupData: SignupFormData = {
      email: values.email,
      password: values.password,
      firstName: values.firstName,
      lastName: values.lastName,
    };

    // If signing up with an invite, we handle navigation manually to ensure it happens
    const redirectUrl = inviteToken
      ? `/invite/accept?token=${encodeURIComponent(inviteToken)}`
      : undefined;

    // When there's an invite token, skip automatic navigation and handle it ourselves
    const result = await signUp(signupData, {
      redirectUrl,
      skipNavigation: !!inviteToken,
    });

    if (result.needsVerification) {
      setShowVerificationPending(true);
    } else if (result.success && inviteToken) {
      // Pass state to signal we just signed up - InviteAccept will check session directly
      navigate(`/invite/accept?token=${encodeURIComponent(inviteToken)}`, {
        replace: true,
        state: { fromSignup: true },
      });
    }
  }

  if (showVerificationPending) {
    // The verification email's link must route the user back to the right destination
    // after they click it. For invites, that's /invite/accept?token=X (to accept the
    // invite with a confirmed session). Otherwise, /create-organization.
    const postVerificationNext = inviteToken
      ? `/invite/accept?token=${encodeURIComponent(inviteToken)}`
      : "/create-organization";
    const verificationRedirectUrl =
      `${window.location.origin}/auth/confirm?next=${encodeURIComponent(postVerificationNext)}` +
      `&email=${encodeURIComponent(userEmail)}`;

    return (
      <EmailVerificationPending
        email={userEmail}
        redirectUrl={verificationRedirectUrl}
        onResendSuccess={() => {
          toast({
            title: t.auth.verificationEmailSent,
            description: t.auth.verificationEmailSentDesc,
          });
        }}
      />
    );
  }

  return (
    <AuthPageLayout contentClassName="max-w-[460px]">
      <AuthPageHeader title={t.signUp.title} description={t.signUp.description} />

      <div className="space-y-5">
        {loadingInvite && (
          <Alert className="rounded-xl">
            <Loader2 className="h-4 w-4 animate-spin" />
            <AlertDescription>{t.auth.loadingInvitation}</AlertDescription>
          </Alert>
        )}
        {inviteDetails && !loadingInvite && (
          <Alert className="rounded-xl border-blue-200 bg-blue-50">
            <Building2 className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-800">
              {t.auth.signingUpToJoin} <strong>{inviteDetails.organizationName}</strong>.
            </AlertDescription>
          </Alert>
        )}

        <form
          onSubmit={form.handleSubmit(onSubmit, (errors) => {
            console.error("Form validation errors:", errors);
            toast({
              variant: "destructive",
              title: t.forms.validation.error,
              description: t.forms.validation.fieldsRequired,
            });
          })}
          className="space-y-[18px]"
          noValidate
        >
          <div className="grid gap-[18px] sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="firstName" className={AUTH_LABEL_CLASSES}>
                {t.signUp.firstName}
              </Label>
              <Input
                id="firstName"
                placeholder={t.signUp.firstName}
                type="text"
                autoComplete="given-name"
                className={AUTH_FIELD_CLASSES}
                aria-required="true"
                aria-invalid={!!form.formState.errors.firstName}
                aria-describedby={form.formState.errors.firstName ? "firstName-error" : undefined}
                {...form.register("firstName")}
              />
              {form.formState.errors.firstName && (
                <p
                  id="firstName-error"
                  role="alert"
                  className="text-[12.5px] font-medium text-destructive"
                >
                  {form.formState.errors.firstName.message}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName" className={AUTH_LABEL_CLASSES}>
                {t.signUp.lastName}
              </Label>
              <Input
                id="lastName"
                placeholder={t.signUp.lastName}
                type="text"
                autoComplete="family-name"
                className={AUTH_FIELD_CLASSES}
                aria-required="true"
                aria-invalid={!!form.formState.errors.lastName}
                aria-describedby={form.formState.errors.lastName ? "lastName-error" : undefined}
                {...form.register("lastName")}
              />
              {form.formState.errors.lastName && (
                <p
                  id="lastName-error"
                  role="alert"
                  className="text-[12.5px] font-medium text-destructive"
                >
                  {form.formState.errors.lastName.message}
                </p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className={AUTH_LABEL_CLASSES}>
              {t.signUp.email}
            </Label>
            <Input
              id="email"
              placeholder={t.signUp.email}
              type="email"
              autoComplete="email"
              readOnly={!!inviteDetails}
              className={cn(AUTH_FIELD_CLASSES, inviteDetails && "cursor-not-allowed bg-muted")}
              aria-required="true"
              aria-invalid={!!form.formState.errors.email}
              aria-describedby={
                form.formState.errors.email
                  ? "signup-email-error"
                  : inviteDetails
                    ? "signup-invite-email-help"
                    : undefined
              }
              {...form.register("email")}
            />
            {inviteDetails && (
              <p id="signup-invite-email-help" className="text-xs text-muted-foreground">
                {t.auth.emailFromInvitation}
              </p>
            )}
            {form.formState.errors.email && (
              <p
                id="signup-email-error"
                role="alert"
                className="text-[12.5px] font-medium text-destructive"
              >
                {form.formState.errors.email.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className={AUTH_LABEL_CLASSES}>
              {t.signUp.password}
            </Label>
            <div className="relative">
              <Input
                id="password"
                placeholder={t.signUp.password}
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                className={cn(AUTH_FIELD_CLASSES, "pr-12")}
                aria-required="true"
                aria-invalid={!!form.formState.errors.password}
                aria-describedby={
                  form.formState.errors.password ? "signup-password-error" : undefined
                }
                {...form.register("password")}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-2 top-1/2 h-9 w-9 -translate-y-1/2 rounded-lg text-neutral-500 hover:text-foreground"
                aria-label={showPassword ? t.forms.hidePassword : t.forms.showPassword}
                onClick={() => setShowPassword((prev) => !prev)}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
            {form.formState.errors.password && (
              <p
                id="signup-password-error"
                role="alert"
                className="text-[12.5px] font-medium text-destructive"
              >
                {form.formState.errors.password.message}
              </p>
            )}
          </div>

          <Button disabled={isLoading} type="submit" className={AUTH_PRIMARY_ACTION_CLASSES}>
            {isLoading ? (
              t.signUp.creatingAccount
            ) : (
              <>
                {t.signUp.submit}
                <ArrowRight className="h-[17px] w-[17px]" />
              </>
            )}
          </Button>
        </form>
      </div>

      <div className="my-6 flex items-center gap-3">
        <span className="h-px flex-1 bg-border" />
        <span className="text-[12.5px] uppercase tracking-wide text-muted-foreground">
          {t.auth.orContinueWith}
        </span>
        <span className="h-px flex-1 bg-border" />
      </div>

      <GoogleSignInButton
        returnUrl={
          inviteToken ? `/invite/accept?token=${encodeURIComponent(inviteToken)}` : "/buildings"
        }
        className={AUTH_SECONDARY_ACTION_CLASSES}
      />

      <p className="mt-6 text-center text-[13.5px] text-muted-foreground">
        {t.signUp.alreadyHaveAccount}{" "}
        <Link to="/login" className={AUTH_LINK_CLASSES}>
          {t.signUp.login}
        </Link>
      </p>
    </AuthPageLayout>
  );
}
